import React, { useEffect, useRef, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Bot, Send, Loader2, ShieldCheck } from "lucide-react";

const SUGGESTIONS = [
  "What is our current diesel inventory?",
  "Which customers owe PCI money?",
  "Which supplier has the highest quantity variance?",
  "Show all pending Delivery Orders.",
  "Which SPAs are approaching expiration?",
  "Which transactions have unresolved reconciliation exceptions?",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  useEffect(() => {
    api.get("/ai/history").then(({ data }) => {
      const hist = data.reverse().flatMap((h) => [
        { role: "user", text: h.question }, { role: "ai", text: h.answer },
      ]);
      setMessages(hist);
    }).catch(() => {});
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const ask = async (question) => {
    const q = (question ?? input).trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/ask", { question: q });
      setMessages((m) => [...m, { role: "ai", text: data.answer }]);
    } catch (e) {
      toast.error(errMsg(e));
      setMessages((m) => [...m, { role: "ai", text: "I couldn't process that request." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-9rem)]">
      <div className="mb-4">
        <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Bot className="h-6 w-6 text-[#12346B]" /> PetroFlow AI Assistant
        </h1>
        <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1">
          <ShieldCheck className="h-3.5 w-3.5" /> Advisory & read-only · respects your permissions · cannot approve, pay, or move petroleum
        </p>
      </div>

      <div className="flex-1 bg-white border border-slate-200 rounded-lg overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm text-slate-500 mb-4">Ask me about inventory, receivables, suppliers, deliveries and more.</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => ask(s)} data-testid={`ai-suggestion-${i}`}
                  className="text-xs text-left px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={`${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${i}`}>
            <div className={`max-w-[75%] rounded-lg px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
              m.role === "user" ? "bg-[#12346B] text-white" : "bg-slate-100 text-slate-800 border border-slate-200"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-slate-100 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Analyzing ERP data…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-3 flex gap-2">
        <Input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about your petroleum operations…" className="flex-1" />
        <Button type="submit" disabled={busy} data-testid="ai-send" className="bg-[#12346B] hover:bg-[#12346B]/90">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}
